<template>
  <span
    ref="triggerEl"
    class="inline-flex"
    @mouseenter="scheduleShow"
    @mouseleave="hide"
    @focusin="showNow"
    @focusout="hide"
    @click="hide"
  >
    <slot />
  </span>

  <!-- Teleported to body, positioned `fixed`. Both callers live inside
       scroll containers — the checklist's `overflow-y-auto` list and the
       board's `overflow-x-auto` column strip — and an absolutely positioned
       tooltip inside those gets clipped at the container's edge, which is
       exactly where the first and last rows sit. Escaping to the body is the
       only way it reliably shows in full. -->
  <Teleport to="body">
    <Transition name="tooltip-fade">
      <!-- aria-hidden on purpose: every trigger already carries its own
           aria-label (see TaskRow.vue), so announcing this too would just
           read the same sentence twice. It is a visual affordance only. -->
      <span
        v-if="visible"
        aria-hidden="true"
        class="fixed z-[100] pointer-events-none max-w-[240px] px-2.5 py-1.5 rounded-lg text-xs font-medium leading-snug text-center shadow-lg
               bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900"
        :style="tooltipStyle"
      >
        {{ text }}
        <!-- A rotated square rather than a border triangle, so it inherits
             the bubble's own background in both themes instead of needing a
             second hard-coded colour per theme. -->
        <span
          class="absolute w-2 h-2 rotate-45 bg-gray-900 dark:bg-gray-100"
          :style="arrowStyle"
        ></span>
      </span>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'

const props = defineProps({
  text: { type: String, required: true },
  // Milliseconds of hover before it appears. Long enough that sweeping the
  // cursor across a dense list doesn't strobe a tooltip per row, short enough
  // that deliberately resting on an icon feels immediate.
  delay: { type: Number, default: 140 },
})

const triggerEl = ref(null)
const visible = ref(false)
const placement = ref('top')
const coords = ref({ left: 0, top: 0 })
const arrowOffset = ref(0)

let showTimer = null

const GAP = 8 // between the trigger and the bubble
const HALF_MAX_WIDTH = 120 // half of max-w-[240px], for viewport clamping
const FLIP_THRESHOLD = 44 // not enough room above? drop underneath instead

function place() {
  const el = triggerEl.value
  if (!el) return

  const rect = el.getBoundingClientRect()
  const triggerCenter = rect.left + rect.width / 2

  // Clamped so a tooltip on an icon near either edge stays fully on screen.
  const left = Math.min(
    Math.max(triggerCenter, HALF_MAX_WIDTH + GAP),
    window.innerWidth - HALF_MAX_WIDTH - GAP,
  )

  // The arrow follows the trigger even when the bubble itself was pushed
  // inward by that clamp, so it keeps pointing at what it describes.
  arrowOffset.value = triggerCenter - left

  placement.value = rect.top < FLIP_THRESHOLD ? 'bottom' : 'top'
  coords.value = {
    left,
    top: placement.value === 'top' ? rect.top - GAP : rect.bottom + GAP,
  }
}

const tooltipStyle = computed(() => ({
  left: `${coords.value.left}px`,
  top: `${coords.value.top}px`,
  transform: placement.value === 'top'
    ? 'translate(-50%, -100%)'
    : 'translate(-50%, 0)',
}))

const arrowStyle = computed(() => ({
  left: `calc(50% + ${arrowOffset.value}px)`,
  marginLeft: '-4px',
  ...(placement.value === 'top' ? { bottom: '-3px' } : { top: '-3px' }),
}))

function showNow() {
  clearTimeout(showTimer)
  place()
  visible.value = true
  // Scrolling would leave the bubble stranded where the trigger used to be.
  // Recomputing on every scroll frame is wasted work for something the user
  // is no longer looking at, so it just goes away.
  window.addEventListener('scroll', hide, { capture: true, passive: true })
  window.addEventListener('resize', hide, { passive: true })
}

function scheduleShow() {
  clearTimeout(showTimer)
  showTimer = setTimeout(showNow, props.delay)
}

function hide() {
  clearTimeout(showTimer)
  visible.value = false
  window.removeEventListener('scroll', hide, { capture: true })
  window.removeEventListener('resize', hide)
}

onBeforeUnmount(hide)
</script>

<style scoped>
/* Opacity only, deliberately. The placement transform lives in the inline
   `:style` (it has to — it depends on runtime measurements), and an inline
   style beats any stylesheet rule that isn't !important. A `transform` here
   would therefore be silently ignored on enter and, worse, would fight the
   positioning if it ever did apply. Fading is enough at this size. */
.tooltip-fade-enter-active {
  transition: opacity 0.12s ease;
}
.tooltip-fade-leave-active {
  transition: opacity 0.09s ease;
}
.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}
</style>
